/**
 * Site entry point — consumed by both `vite dev` (client hydration) and
 * `vite-ssg build` (static pre-render pass).
 *
 * Uses createBrowserRouter for the client and createMemoryRouter for
 * SSR (vite-ssg pre-render in Node where `document` doesn't exist).
 */
import './styles/global.css';
import './styles/bankers.css';
import posthog from 'posthog-js';
import { createRoot, hydrateRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createMemoryRouter,
  RouterProvider,
} from 'react-router-dom';
import { routes } from './App';

const isClient = typeof document !== 'undefined';

export function createApp(url = '/') {
  const router = isClient
    ? createBrowserRouter(routes)
    : createMemoryRouter(routes, { initialEntries: [url] });

  const tree = (
    <RouterProvider router={router} />
  );

  return {
    routes,
    router,
    render: (container: HTMLElement): void => {
      createRoot(container).render(tree);
    },
    hydrate: (container: HTMLElement): void => {
      hydrateRoot(container, tree);
    },
  };
}

// PostHog analytics — only in the browser, only if the key is set
if (isClient && import.meta.env.VITE_POSTHOG_KEY) {
  const apiHost = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://eu.i.posthog.com';
  posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
    api_host: apiHost,
    ui_host: 'https://eu.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    autocapture: true,
    loaded: (ph) => {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        ph.opt_out_capturing();
      }
    },
  });
}

if (isClient) {
  const container = document.getElementById('root');
  if (container) {
    const app = createApp();
    if (container.hasChildNodes()) {
      app.hydrate(container);
    } else {
      app.render(container);
    }
  }
}

export default createApp;
